import type { CommandId, PullRequestEditResult } from "@afar-research/contracts";

interface CommandState<Input> {
  command: Input | null;
  pending: boolean;
  result: PullRequestEditResult | null;
}

/** Preserve the original command identity until its hosted outcome is settled. */
export async function runPullRequestCommand<Input extends { readonly commandId: CommandId }>({
  session,
  input,
  enabled,
  execute,
  onStart,
  onAccepted,
}: {
  readonly session: {
    getState: () => CommandState<Input>;
    setState: (state: Partial<CommandState<Input>>) => void;
  };
  readonly input: Input;
  readonly enabled: boolean;
  readonly execute: (input: Input) => Promise<PullRequestEditResult>;
  readonly onStart?: () => void;
  readonly onAccepted?: () => void;
}) {
  if (!enabled || session.getState().pending) return;
  session.setState({ command: input, pending: true, result: null });
  onStart?.();
  try {
    const result = await execute(input);
    session.setState({ result });
    if (result.status === "settled" && result.receipt.status === "accepted") onAccepted?.();
  } catch {
    session.setState({
      result: {
        status: "uncertain",
        commandId: input.commandId,
        message:
          "The outcome is uncertain. Check outcome to reconcile without repeating the write.",
      },
    });
  } finally {
    session.setState({ pending: false });
  }
}
